import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { Table } from "../../../../ui/components/Table";
import { TextField } from "../../../../ui/components/TextField";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { SortDropdown } from "../../../../shared/presentation/components/SortDropdown";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import {
  FeatherSearch,
  FeatherChevronLeft,
  FeatherChevronRight,
  FeatherArrowLeft,
} from "@subframe/core";
import { recoContainer } from "../../di/container";
import {
  CreditSummaryEntity,
  PaginatedResponse,
  ListParams,
} from "../../domain/entities/CreditSummaryEntity";
import ViewOutstandingBillsModal from "../components/ViewOutstandingBillsModal";

export const VendorOutstandingBillsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currencyFilter = searchParams.get("currency") || undefined;

  const [credits, setCredits] = useState<CreditSummaryEntity[]>([]);
  const [pagination, setPagination] = useState<
    PaginatedResponse<CreditSummaryEntity>["pagination"] | null
  >(null);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("outstanding_documents_count");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [isSearching, setIsSearching] = useState(false);
  const [outstandingBillsModalOpen, setOutstandingBillsModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<{
    vendorId: string;
    vendorName: string;
  } | null>(null);

  // Format date helper
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Format number in Indian/lakh format
  const formatIndianNumber = (num: number): string => {
    if (num >= 1000) {
      return num.toLocaleString("en-IN");
    }
    return num.toString();
  };

  // Sort options
  const sortOptions = [
    {
      value: "outstanding_documents_count_desc",
      label: "Outstanding Count: Highest First",
      by: "outstanding_documents_count",
      order: "desc",
    },
    {
      value: "outstanding_documents_count_asc",
      label: "Outstanding Count: Lowest First",
      by: "outstanding_documents_count",
      order: "asc",
    },
    {
      value: "entity_name_asc",
      label: "Vendor Name: A-Z",
      by: "entity_name",
      order: "asc",
    },
    {
      value: "entity_name_desc",
      label: "Vendor Name: Z-A",
      by: "entity_name",
      order: "desc",
    },
  ];

  const fetchList = async (params: ListParams) => {
    try {
      setLoading(true);

      const useCase = recoContainer.getVendorOutstandingBills();
      const response = await useCase.execute(params);

      setCredits(response.data);
      setPagination(response.pagination);
    } catch (err: any) {
      console.error("Error fetching vendor outstanding bills:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    fetchList({
      page: 1,
      limit: itemsPerPage,
      sortBy,
      sortOrder,
      currencyCode: currencyFilter,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currencyFilter]);

  // Debounced search effect
  useEffect(() => {
    if (searchTerm.trim()) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }

    const timeoutId = setTimeout(() => {
      fetchList({
        page: 1,
        limit: itemsPerPage,
        search: searchTerm.trim() || undefined,
        sortBy,
        sortOrder,
        currencyCode: currencyFilter,
      }).finally(() => {
        setIsSearching(false);
      });
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, sortBy, sortOrder, itemsPerPage, currencyFilter]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (pagination?.totalPages || 0)) {
      setCurrentPage(page);
      fetchList({
        page,
        limit: itemsPerPage,
        search: searchTerm.trim() || undefined,
        sortBy,
        sortOrder,
        currencyCode: currencyFilter,
      });
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasMore) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <Button
            variant="brand-secondary"
            icon={<FeatherArrowLeft />}
            onClick={() => navigate("/dashboard/reco")}
          >
            Back to Reco Dashboard
          </Button>
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Vendor Outstanding Bills
                {currencyFilter ? ` (${currencyFilter})` : ""}
              </span>
              <span className="text-body font-body text-subtext-color">
                {currencyFilter
                  ? `Vendors with outstanding ${currencyFilter} bills`
                  : "View all vendors with outstanding bills"}
              </span>
            </div>
          </div>

          {/* Reminder Message */}
          <div className="flex w-full items-center gap-3 rounded-md border border-solid border-warning-border bg-warning-background px-4 py-3">
            <span className="text-body font-body text-warning-font">
              Please send the money for outstanding bills.
            </span>
          </div>

          {/* Search and Sort Controls */}
          <div className="flex w-full items-center justify-end gap-2">
            <TextField
              className="h-auto grow shrink-0 basis-0"
              variant="filled"
              label=""
              helpText=""
              icon={<FeatherSearch />}
            >
              <TextField.Input
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(event.target.value)
                }
              />
            </TextField>
            <SortDropdown
              currentSort={{
                by: sortBy,
                order: sortOrder,
                label:
                  sortOptions.find(
                    (opt) => opt.by === sortBy && opt.order === sortOrder
                  )?.label || "Outstanding Count: Highest First",
              }}
              onSortChange={(by, order, label) => {
                setSortBy(by);
                setSortOrder(order as "asc" | "desc");
              }}
              options={sortOptions}
              disabled={loading}
            />
          </div>
        </div>

        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 bg-default-background px-8 py-6 overflow-auto">
          {/* Table */}
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell className="w-64">
                  Vendor Name
                </Table.HeaderCell>
                <Table.HeaderCell className="w-40 text-center">
                  Outstanding Bills
                </Table.HeaderCell>
                <Table.HeaderCell className="w-40 text-center">
                  Outstanding Amount
                </Table.HeaderCell>
                <Table.HeaderCell className="w-56">
                  Last Updated
                </Table.HeaderCell>
              </Table.HeaderRow>
            }
          >
            {loading ? (
              isSearching ? (
                <Table.Row>
                  <Table.Cell colSpan={4} className="text-center py-12">
                    <div className="flex w-full items-center justify-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                        <span className="text-body font-body text-subtext-color">
                          Searching vendors...
                        </span>
                      </div>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : (
                <TableSkeletonRows rowCount={itemsPerPage} columnCount={4} />
              )
            ) : credits.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={4}>
                  <div className="flex w-full items-center justify-center py-8">
                    <span className="text-body font-body text-neutral-500">
                      {searchTerm.trim()
                        ? "No vendors match your search"
                        : "No vendors with outstanding bills found"}
                    </span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : (
              credits.map((credit) => (
                <Table.Row key={credit.entityId}>
                  <Table.Cell className="w-64">
                    <span className="text-body-bold font-body-bold text-default-font">
                      {credit.entityName}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-40 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span className="inline-block min-w-[3ch] text-right tabular-nums text-body-bold font-body-bold text-error-font">
                        {formatIndianNumber(credit.outstandingDocumentsCount || 0)}
                      </span>
                      {(credit.outstandingDocumentsCount ?? 0) > 0 && (
                          <button
                            className="text-caption font-caption text-brand-600 hover:text-brand-700 hover:underline cursor-pointer shrink-0"
                            onClick={() => {
                              setSelectedVendor({
                                vendorId: credit.entityId,
                                vendorName: credit.entityName,
                              });
                              setOutstandingBillsModalOpen(true);
                            }}
                          >
                            View
                          </button>
                        )}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="w-40 text-center">
                    <span className="text-body-bold font-body-bold text-error-font">
                      {credit.currencySymbol}
                      {formatIndianNumber(credit.outstandingAmount || 0)}
                    </span>
                  </Table.Cell>
                  <Table.Cell className="w-56">
                    <span className="text-body font-body text-subtext-color">
                      {formatDate(credit.lastUpdated)}
                    </span>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table>

          {/* Pagination Controls */}
          {(pagination?.totalPages || 0) > 1 && !loading && (
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Showing {((pagination?.page || 1) - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(
                    (pagination?.page || 1) * itemsPerPage,
                    pagination?.total || 0
                  )}{" "}
                  of {pagination?.total || 0} results
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  icon={<FeatherChevronLeft />}
                >
                  Previous
                </Button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from(
                    { length: Math.min(5, pagination?.totalPages || 0) },
                    (_, i) => {
                      let pageNum: number;
                      if ((pagination?.totalPages || 0) <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (
                        currentPage >=
                        (pagination?.totalPages || 0) - 2
                      ) {
                        pageNum = (pagination?.totalPages || 0) - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          variant={
                            currentPage === pageNum
                              ? "brand-primary"
                              : "neutral-tertiary"
                          }
                          size="small"
                          onClick={() => handlePageChange(pageNum)}
                          className="min-w-[32px]"
                        >
                          {pageNum}
                        </Button>
                      );
                    }
                  )}
                </div>

                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={handleNextPage}
                  disabled={!pagination?.hasMore}
                  icon={<FeatherChevronRight />}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {selectedVendor && (
        <ViewOutstandingBillsModal
          open={outstandingBillsModalOpen}
          vendorId={selectedVendor.vendorId}
          vendorName={selectedVendor.vendorName}
          onOpenChange={(open) => {
            setOutstandingBillsModalOpen(open);
            if (!open) {
              setSelectedVendor(null);
            }
          }}
        />
      )}
    </DefaultPageLayout>
  );
};
